/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React, {useEffect} from 'react';
import {useMachine} from "@xstate/react";

import {
  NxButtonBar,
  NxErrorAlert,
  NxFontAwesomeIcon,
  NxGrid,
  NxH2,
  NxH3,
  NxLoadWrapper
} from "@sonatype/react-shared-components";
import {ExtJS} from '@sonatype/nexus-ui-plugin';
import {faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";

import MaliciousRiskOnDiskMachine from "./MaliciousRiskOnDiskMachine";
import UIStrings from "../../../../constants/UIStrings";
import "./MaliciousRiskOnDisk.scss";
import FeatureFlags from '../../../../constants/FeatureFlags';

const {
  CLM,
  MALWARE_RISK_ENABLED,
  MALWARE_RISK_ON_DISK_ENABLED,
  MALWARE_RISK_ON_DISK_NONADMIN_OVERRIDE_ENABLED
} = FeatureFlags;

const {
  TITLE_PLURAL,
  TITLE_SINGULAR,
  DESCRIPTION,
  CONTACT_SONATYPE,
  VIEW_MALWARE_RISK,
} = UIStrings.MALICIOUS_RISK.RISK_ON_DISK;

function MalwareButton({isProEdition, isMalwareRiskEnabled, isIqServerEnabled}) {
  if (isProEdition && isMalwareRiskEnabled && isIqServerEnabled) {
    return <a className="nx-btn nx-btn--error" href="#browse/malwarerisk">{VIEW_MALWARE_RISK}</a>;
  } else if (isProEdition) {
    return <a className="nx-btn nx-btn--error" href={CONTACT_SONATYPE.URL.PRO} target="_blank">{CONTACT_SONATYPE.TEXT}</a>;
  } else {
    return <a className="nx-btn nx-btn--error" href={CONTACT_SONATYPE.URL.OSS} target="_blank">{CONTACT_SONATYPE.TEXT}</a>;
  }
}

function MaliciousRiskOnDiskContent({user, props}) {
  const [state, send] = useMachine(MaliciousRiskOnDiskMachine, {devtools: true});
  const {maliciousRiskOnDisk, loadError} = state.context;
  const isLoading = state.matches('chooseInitialState') || state.matches('loading');
  const closeMalwareBanner = state.matches('close');

  const isAdmin = user?.administrator;
  const isProEdition = ExtJS.isProEdition();
  const isIqServerEnabled = ExtJS.state().getValue(CLM)?.enabled;
  const isMalwareRiskEnabled = ExtJS.state().getValue(MALWARE_RISK_ENABLED);
  const isMalwareRemediationPage = window.location.hash.includes('#browse/malwarerisk');

  const riskOnDiskCount = maliciousRiskOnDisk?.totalCount ?? 0;
  const showWarningAlert = riskOnDiskCount > 0 && !closeMalwareBanner && !isMalwareRemediationPage;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.location.hash.includes('#browse/browse') || window.location.hash.includes('#browse/search')) {
        props.rerender(riskOnDiskCount, closeMalwareBanner);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [riskOnDiskCount, closeMalwareBanner]);

  function retry() {
    send({type: 'RETRY'});
  }

  function dismiss() {
    send({type: 'DISMISS'});
  }

  return (
      <NxLoadWrapper loading={isLoading} error={loadError} retryHandler={retry}>
        {showWarningAlert && <div className="risk-on-disk-container">
          <NxErrorAlert className="risk-on-disk-alert" onClose={dismiss}>
            <div className="risk-on-disk-content">
              <div className="malware-components-count">
                <NxFontAwesomeIcon icon={faExclamationTriangle}/>
                <NxH2>{riskOnDiskCount.toLocaleString()}</NxH2>
                <NxH3>{riskOnDiskCount > 1 ? TITLE_PLURAL : TITLE_SINGULAR}</NxH3>
              </div>
              <NxGrid.Column className='risk-on-disk-alert-description'>
                <NxGrid.ColumnSection>
                  <p>{DESCRIPTION.CONTENT}</p>
                </NxGrid.ColumnSection>
                {!isAdmin &&
                    <NxGrid.ColumnSection>
                      <p>{DESCRIPTION.ADDITIONAL_NON_ADMIN_CONTENT}</p>
                    </NxGrid.ColumnSection>
                }
              </NxGrid.Column>
            </div>
            {isAdmin && <NxButtonBar>
              <MalwareButton isProEdition={isProEdition}
                             isMalwareRiskEnabled={isMalwareRiskEnabled}
                             isIqServerEnabled={isIqServerEnabled}/>
            </NxButtonBar>}
          </NxErrorAlert>
        </div>}
        {isMalwareRemediationPage && <div className={`malware-components-count ${riskOnDiskCount === 0 ? 'zero' : ''}`}>
            {riskOnDiskCount > 0 && <NxFontAwesomeIcon icon={faExclamationTriangle}/>}
            <NxH2>{riskOnDiskCount.toLocaleString()}</NxH2>
            <NxH3>{riskOnDiskCount > 1 ? TITLE_PLURAL : TITLE_SINGULAR}</NxH3>
        </div>}
      </NxLoadWrapper>
  );
}

export default function MaliciousRiskOnDisk(props) {
  const isRiskOnDiskEnabled = ExtJS.state().getValue(MALWARE_RISK_ON_DISK_ENABLED);
  const user = ExtJS.useUser();
  const userIsLogged = user ?? false;
  const showMaliciousRiskOnDisk = userIsLogged && isRiskOnDiskEnabled;

  const isRiskOnDiskNoneAdminOverrideEnabled = ExtJS.state().getValue(MALWARE_RISK_ON_DISK_NONADMIN_OVERRIDE_ENABLED);
  const isAdmin = user && user.administrator;
  const shouldHideForNonAdmin = isRiskOnDiskNoneAdminOverrideEnabled && !isAdmin;

  if (!showMaliciousRiskOnDisk || shouldHideForNonAdmin) {
    return null;
  }
  return <MaliciousRiskOnDiskContent user={user} props={props}/>;
}
